import { colors } from '@nandosaus/constants';
import { variant as styledVariant } from 'styled-system';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import StyledSystemPropTypes from '@styled-system/prop-types';

import { Box, Flex } from '../../grid';
import { LegacyCardImage } from './legacy-card-image';
import { cmsImagePropTypes } from '../../../prop-types';
import { ConditionalLink } from '../conditional-link';

const Card = ({
  as,
  children,
  backgroundColor,
  contentHeight,
  contentWidth,
  flexDirection,
  flexWrap,
  href,
  image,
  imageHeight,
  imageWidth,
  isDisabled,
  isEligibleForDeliveryDiscount,
  isDiscountApplied,
  deliveryDiscountSpacing,
  isItemInCart,
  justifyContent,
  onClick,
  overlayContent,
  padding,
}) => (
  <ConditionalLink href={href} as={as}>
    <Wrapper
      variant={backgroundColor}
      flexDirection={flexDirection}
      flexWrap={flexWrap}
      height="100%"
      onClick={onClick}
      overlayContent={overlayContent}
      position="relative"
      role={onClick ? 'button' : undefined}
      width="100%"
    >
      <LegacyCardImage
        isDisabled={isDisabled}
        image={image}
        imageHeight={imageHeight}
        imageWidth={imageWidth}
        isItemInCart={isItemInCart}
        position={overlayContent ? 'absolute' : 'static'}
        isEligibleForDeliveryDiscount={isEligibleForDeliveryDiscount}
        isDiscountApplied={isDiscountApplied}
        deliveryDiscountSpacing={deliveryDiscountSpacing}
      />
      {overlayContent && <Overlay />}
      <Flex
        flexDirection="column"
        height={contentHeight}
        justifyContent={justifyContent}
        p={padding}
        width={contentWidth}
        position={overlayContent ? 'absolute' : 'static'}
      >
        {children}
      </Flex>
    </Wrapper>
  </ConditionalLink>
);

const backgroundColorVariants = styledVariant({
  variants: {
    light: {
      backgroundColor: colors.white,
      color: colors.black,
    },
    dark: {
      backgroundColor: colors.black,
      color: colors.white,
    },
  },
});

const Wrapper = styled(Flex)`
  ${backgroundColorVariants};
  ${({ role }) => role === 'button' && `cursor: pointer;`};
`;

const Overlay = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: inline-block;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 47.28%, #000000 95.86%);
`;

Card.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node,
  backgroundColor: PropTypes.oneOf(['light', 'dark']),
  contentHeight: PropTypes.oneOfType([PropTypes.string, StyledSystemPropTypes.layout.height]),
  contentWidth: StyledSystemPropTypes.layout.width,
  flexDirection: StyledSystemPropTypes.flexbox.flexDirection,
  flexWrap: StyledSystemPropTypes.flexbox.flexWrap,
  href: PropTypes.string,
  image: PropTypes.oneOfType([cmsImagePropTypes, PropTypes.arrayOf(cmsImagePropTypes)]),
  imageHeight: StyledSystemPropTypes.layout.height,
  imageWidth: StyledSystemPropTypes.layout.width,
  isDisabled: PropTypes.bool,
  isEligibleForDeliveryDiscount: PropTypes.bool,
  isDiscountApplied: PropTypes.bool,
  deliveryDiscountSpacing: StyledSystemPropTypes.space.margin,
  isItemInCart: PropTypes.bool,
  justifyContent: PropTypes.string,
  onClick: PropTypes.func,
  overlayContent: PropTypes.bool,
  padding: StyledSystemPropTypes.space.padding,
};

Card.defaultProps = {
  as: null,
  children: null,
  backgroundColor: 'light',
  contentHeight: '100%',
  contentWidth: undefined,
  flexDirection: 'row',
  flexWrap: 'wrap',
  href: undefined,
  image: null,
  imageHeight: undefined,
  imageWidth: undefined,
  isDisabled: false,
  isEligibleForDeliveryDiscount: false,
  isDiscountApplied: false,
  deliveryDiscountSpacing: null,
  isItemInCart: false,
  justifyContent: 'space-between',
  onClick: undefined,
  overlayContent: false,
  padding: { xs: 1 },
};

/**
 * @deprecated
 */
const LegacyCard = Card;
export { LegacyCard };
